import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import CurrentPageContext from 'website/contexts/CurrentPageContext';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { Modal } from '@storable/canaveral-components';
import Button from '../../../../modules/react/components/stateless/Button';
import Select from '../../../../modules/react/components/stateful/Select';
import Ribbon from '../sections/Banner/Ribbon';
import BannerInput, { RIBBON } from '../sections/Banner/BannerInput';
import { getForegroundColor, getBackgroundColor } from '../../utils/colorSelect';
import { WHITE, PRIMARY, SECONDARY, TERTIARY, MAIN } from '../../utils/colorSelect/constants';
import styles from './PromotionBannerModal.scss';

const prepareState = ({
  selectedPromos, fontColor, bgColor, displayStyle,
}) => {
  const state = {
    selectedPromos,
    fontColor,
    bgColor,
    displayStyle,
  };

  return state;
};

export default class PromotionBannerModal extends React.Component {
  static propTypes = {
    handleChange: PropTypes.func,
    showModal: PropTypes.bool,
    onClose: PropTypes.func,
    onCancelEdit: PropTypes.func,
    onFinishEdit: PropTypes.func,
    colors: PropTypes.object,
    bgColor: PropTypes.string,
    fontColor: PropTypes.string,
    displayStyle: PropTypes.string,
    selectedPromos: PropTypes.array,
    promos: PropTypes.object,
  };

  static contextType = CurrentPageContext;

  static defaultProps = {
    selectedPromos: [],
  };

  state = prepareState(this.props);

  handleCancelEdit = () => {
    this.props.onClose();
    this.props.onCancelEdit();
  };

  handleChange = (id, { target }) => {
    this.props.handleChange(id, { target });
  };

  editPromoText = ({ target }) => {
    const index = target.name === 'topPromo' ? 0 : 1;
    const { selectedPromos } = this.state;
    selectedPromos[index].description = target.value;
    this.setState({ selectedPromos });
  };

  save = () => {
    const {
      selectedPromos, fontColor, bgColor, displayStyle,
    } = this.state;
    const { facilityId } = this.props;
    this.props.onFinishEdit({
      selectedPromos, fontColor, bgColor, displayStyle, facilityId,
    });
  };

  selectPromo = (promo, index) => {
    const { selectedPromos } = this.state;
    selectedPromos[index] = promo;
    this.setState({ selectedPromos });
  };

  selectBannerStyle = (displayStyle) => {
    this.setState({ displayStyle });
  };

  selectBackgroundColor = ({ value }) => {
    this.setState({ bgColor: value });
  };

  selectFontColor = ({ value }) => {
    this.setState({ fontColor: value });
  };

  renderOption() {
    return option => (
      <span>{option.label}</span>
    );
  }

  renderPreview() {
    const {
      selectedPromos, displayStyle, bgColor, fontColor,
    } = this.state;
    const showPromos = selectedPromos.length && (selectedPromos[0] || selectedPromos[1]);
    const { foregroundClass } = getForegroundColor(fontColor, this.context);
    const { backgroundClass, backgroundHex } = getBackgroundColor(bgColor, this.context);

    if (displayStyle === RIBBON) {
      return (
        <div className={styles.ribbonContainer}>
          <div className={cx(styles.ribbon, styles.svg)}>
            <Ribbon color={backgroundHex} />
          </div>
          {showPromos ?
            <div className={styles.ribbonText}>
              {selectedPromos.map((promo, index) => this.renderPromo(promo, index, foregroundClass))}
            </div>
            :
            <div className={styles.ribbonText}>
              <h4 className={foregroundClass} style={{ textAlign: 'center' }}>Select a promotion to feature.</h4>
            </div>
          }
        </div>
      );
    }

    return (
      <div className={cx(styles.promos, backgroundClass)} style={{ marginTop: 20 }}>
        {showPromos ?
          <div>
            {selectedPromos.map((promo, index) => this.renderPromo(promo, index, foregroundClass))}
          </div>
          :
          <h4
            className={cx(styles.bannerHeader, foregroundClass)}
            style={{ textAlign: 'center' }}
          >Select a promotion to feature.
          </h4>
        }
      </div>
    );
  }

  renderPromo = (promo, index, fontClass) => {
    if (!promo) return null;

    return (
      <Row className={cx(styles.promos, fontClass)} key={index}>
        <span>
          Use promo code <b>{promo.name}</b> for <b>{promo.description}</b>
        </span>

        {promo.endDate ?
          <span className={styles.endDate}>
            (expires on {promo.endDate})
          </span>
          :
          null
        }
      </Row>
    );
  };

  renderPromoOption() {
    return promo => (
      <span>{promo.name} - {promo.description}</span>
    );
  }

  renderColor(marginTop) {
    return option => (
      <span>
        <div
          style={{ backgroundColor: option.code, marginTop }}
          className={styles.colorBlock}
        />
        {option.label}
      </span>
    );
  }

  render() {
    const { promos, showModal } = this.props;
    const { selectedPromos, bgColor, fontColor } = this.state;
    const { primaryColor, secondaryColor, tertiaryColor } = this.context;
    const preview = this.renderPreview();
    const fontColors = [
      { value: WHITE, label: 'White', code: '#fff' },
      { value: MAIN, label: 'Main text color', code: '#333' },
      { value: PRIMARY, label: 'primary', code: primaryColor },
      { value: SECONDARY, label: 'secondary', code: secondaryColor },
      { value: TERTIARY, label: 'tertiary', code: tertiaryColor },
    ];
    const bgColors = [
      { value: WHITE, label: 'White', code: '#fff' },
      { value: PRIMARY, label: 'primary', code: primaryColor },
      { value: SECONDARY, label: 'secondary', code: secondaryColor },
      { value: TERTIARY, label: 'tertiary', code: tertiaryColor },
    ];
    const { backgroundHex } = getBackgroundColor(bgColor, this.context);

    return (
      <Modal
        show={showModal}
        onDismiss={this.handleCancelEdit}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Promo Banner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs id="promotion-banner-edit-modal-tab" defaultActiveKey={1} className={styles.fieldLabel}>
            <Tab title="Promo" eventKey={1}>
              {preview}
              <Row className={styles.settingRow}>
                <h4
                  className={styles.selectPromoText}
                  style={{ marginLeft: 15 }}
                >Select promotions to display.
                </h4>
                <Col md={6}>
                  <div className={styles.fieldLabel}>Top</div>
                  <Select
                    autoBlur
                    clearable
                    instanceId="promo-banner-top"
                    onChange={(promo) => { this.selectPromo(promo, 0); }}
                    optionRenderer={this.renderPromoOption()}
                    valueRenderer={this.renderPromoOption()}
                    options={Object.keys(promos).map(key => promos[key])}
                    value={selectedPromos[0]}
                  />
                </Col>
                <Col md={6}>
                  <div className={styles.fieldLabel}>Bottom</div>
                  <Select
                    autoBlur
                    clearable
                    instanceId="promo-banner-bottom"
                    onChange={(promo) => { this.selectPromo(promo, 1); }}
                    optionRenderer={this.renderPromoOption()}
                    valueRenderer={this.renderPromoOption()}
                    options={Object.keys(promos).map(key => promos[key])}
                    value={selectedPromos[1]}
                  />
                </Col>
              </Row>
            </Tab>
            <Tab title="Banner Style" eventKey={2}>
              {preview}
              <Row className={styles.settingRow}>
                <Col md={9}>
                  <div className={styles.fieldLabel}>Color</div>
                  <Select
                    autoBlur
                    className={styles.selectColorOptions}
                    clearable={false}
                    instanceId="background-color-selector"
                    onChange={this.selectBackgroundColor}
                    optionRenderer={this.renderColor('3px')}
                    valueRenderer={this.renderColor('10px')}
                    options={bgColors}
                    value={bgColor}
                  />
                </Col>
              </Row>
              <Row className={styles.settingRow}>
                <Col md={12}>
                  <BannerInput
                    color={backgroundHex}
                    input={{ onChange: style => this.selectBannerStyle(style) }}
                  />
                </Col>
              </Row>
            </Tab>
            <Tab title="Customize" eventKey={3}>
              {preview}
              <Row className={styles.settingRow}>
                <Col md={5}>
                  <div className={styles.fieldLabel}>Font Color</div>
                  <Select
                    autoBlur
                    className={styles.selectColorOptions}
                    clearable={false}
                    instanceId="font-color-selector"
                    onChange={this.selectFontColor}
                    optionRenderer={this.renderColor('3px')}
                    valueRenderer={this.renderColor('10px')}
                    options={fontColors}
                    value={fontColor}
                  />
                </Col>
              </Row>
              <Row className={styles.settingRow}>
                <Col md={6}>
                  <div className={styles.fieldLabel}>Edit Top Text</div>
                  <input
                    type="text"
                    name="topPromo"
                    placeholder="Select a promotion from the Promo tab"
                    color="#979797"
                    disabled={!selectedPromos[0]}
                    className={styles.input}
                    value={selectedPromos[0] ? selectedPromos[0].description : ''}
                    onChange={(text) => { this.editPromoText(text); }}
                  />
                </Col>
                <Col md={6}>
                  <div className={styles.fieldLabel}>Edit Bottom Text</div>
                  <input
                    type="text"
                    name="bottomPromo"
                    placeholder="Select a promotion from the Promo tab"
                    color="#979797"
                    disabled={!selectedPromos[1]}
                    className={styles.input}
                    value={selectedPromos[1] ? selectedPromos[1].description : ''}
                    onChange={(text) => { this.editPromoText(text); }}
                  />
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" onClick={this.handleCancelEdit}>Cancel</Button>
          <Button
            onClick={this.save}
            variant="success"
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
